import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  FileDown,
  FileText,
  Mic,
  Paperclip,
  Pencil,
  Presentation,
  Trash2,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { canEdit, watchProject } from '../services/projects';
import { watchFormulations } from '../services/formulations';
import { watchCaptures } from '../services/captures';
import { watchReadings } from '../services/readings';
import type { Reading } from '../services/readings';
import {
  addNote,
  deleteAttachment,
  deleteExperiment,
  deleteNote,
  editNote,
  importWordNote,
  setActiveExperiment,
  uploadAttachment,
  watchAttachments,
  watchExperiment,
  watchNotes,
} from '../services/experiments';
import { MAX_NOTE_CHARS } from '../services/experiments';
import type { Attachment, Experiment, Note } from '../services/experiments';
import type { Capture, Formulation, Project } from '../lib/types';
import { Badge, Button, EmptyState, Modal, Spinner, Textarea } from './ui';
import CaptureBar from './CaptureBar';
import ConfirmationScreen from './ConfirmationScreen';
import type { ConfirmationSource } from './ConfirmationScreen';
import ReaderCapture from './ReaderCapture';
import type { ReaderSession } from './ReaderCapture';
import ReadingsView from './ReadingsView';
import ExperimentDialog from './ExperimentDialog';
import { shortDate } from './NotebookSection';
import HoldToTalk from './HoldToTalk';
import VocabularyDialog from './VocabularyDialog';

/**
 * One experiment: its aim, the data captured while it was open, the notes
 * written or spoken about it, and the documents attached to it.
 *
 * Capturing here works exactly as on the project page; the only difference is
 * that everything saved while this page is open is stamped with the
 * experiment, which is how its data finds its way back here and into exports.
 */
export default function ExperimentView() {
  const { projectId = '', experimentId = '' } = useParams();
  const { user } = useAuth();
  const uid = user?.uid;
  const navigate = useNavigate();

  const [project, setProject] = useState<Project | null | 'missing'>(null);
  const [experiment, setExperiment] = useState<Experiment | null | 'missing'>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [readings, setReadings] = useState<Reading[]>([]);
  const [captures, setCaptures] = useState<Capture[]>([]);
  const [formulations, setFormulations] = useState<Formulation[]>([]);
  const [pendingConfirmation, setPendingConfirmation] = useState<ConfirmationSource | null>(null);
  const [readerSession, setReaderSession] = useState<ReaderSession | null>(null);
  const [editingExperiment, setEditingExperiment] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [exporting, setExporting] = useState<'pdf' | 'xlsx' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(
    () => watchProject(projectId, (p) => setProject(p ?? 'missing'), () => setProject('missing')),
    [projectId],
  );
  useEffect(
    () => watchExperiment(projectId, experimentId, (e) => setExperiment(e ?? 'missing')),
    [projectId, experimentId],
  );
  useEffect(() => watchNotes(projectId, experimentId, setNotes), [projectId, experimentId]);
  useEffect(() => watchAttachments(projectId, experimentId, setAttachments), [projectId, experimentId]);
  useEffect(() => watchReadings(projectId, setReadings), [projectId]);
  useEffect(() => watchCaptures(projectId, setCaptures), [projectId]);
  useEffect(() => watchFormulations(projectId, setFormulations), [projectId]);

  // Everything captured while this page is open belongs to this experiment.
  useEffect(() => setActiveExperiment(projectId, experimentId), [projectId, experimentId]);

  const data = useMemo(
    () => readings.filter((r) => r.experimentId === experimentId),
    [readings, experimentId],
  );

  if (project === 'missing' || experiment === 'missing') {
    return (
      <EmptyState
        title="Experiment not available"
        body="It may have been deleted, or your access to the project removed."
        action={
          <Link to={project === 'missing' ? '/' : `/p/${projectId}`}>
            <Button variant="secondary" icon={<ArrowLeft size={16} />}>
              Back
            </Button>
          </Link>
        }
      />
    );
  }
  if (!uid || !user || project === null || experiment === null) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const isOwner = project.ownerUid === uid;
  const readOnly = !canEdit(project, uid);
  const readerMode = Array.isArray(project.readers);
  const projectReaders = project.readers ?? [];
  const showCaptureBar = !readOnly && (!readerMode || projectReaders.length > 0);
  const me = { uid, name: user.displayName ?? user.email ?? null };
  const canDeleteExperiment = !readOnly && (isOwner || experiment.createdByUid === uid);

  async function exportPdf() {
    if (project === null || project === 'missing' || experiment === null || experiment === 'missing') return;
    setExporting('pdf');
    try {
      const { exportNotebookDocx } = await import('../services/exportNotebook');
      await exportNotebookDocx(project, [experiment], readings, projectReaders);
    } catch (e) {
      setNotice((e as Error)?.message ?? 'Could not build the Word file.');
    } finally {
      setExporting(null);
    }
  }

  async function exportXlsx() {
    if (project === null || project === 'missing' || experiment === null || experiment === 'missing') return;
    setExporting('xlsx');
    try {
      const { exportExperimentReadings } = await import('../services/exportXlsx');
      if (!exportExperimentReadings(project, experiment, captures, readings)) {
        setNotice('No data has been captured in this experiment yet.');
      }
    } finally {
      setExporting(null);
    }
  }

  return (
    <div
      style={
        showCaptureBar ? { paddingBottom: 'calc(11rem + env(safe-area-inset-bottom, 0px))' } : undefined
      }
    >
      <div className="mb-5">
        <Link
          to={`/p/${projectId}`}
          className="mb-1 inline-flex items-center gap-1 text-sm text-ink-400 hover:text-ink-100 pointer-coarse:min-h-11"
        >
          <ArrowLeft size={14} />
          {project.name}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs tabular-nums text-ink-400">{shortDate(experiment.date)}</p>
            <h1 className="text-xl font-semibold">{experiment.title || 'Untitled experiment'}</h1>
            {experiment.aim && (
              <p className="mt-1 max-w-2xl whitespace-pre-wrap text-sm text-ink-300">{experiment.aim}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              icon={<Download size={16} />}
              loading={exporting === 'xlsx'}
              disabled={data.length === 0}
              onClick={() => void exportXlsx()}
            >
              Data .xlsx
            </Button>
            <Button
              variant="secondary"
              icon={<FileDown size={16} />}
              loading={exporting === 'pdf'}
              onClick={() => void exportPdf()}
            >
              Notes (Word)
            </Button>
            {!readOnly && (
              <Button variant="ghost" icon={<Pencil size={16} />} onClick={() => setEditingExperiment(true)}>
                Edit
              </Button>
            )}
            {canDeleteExperiment && (
              <Button variant="ghost" icon={<Trash2 size={16} />} onClick={() => setConfirmDelete(true)}>
                Delete
              </Button>
            )}
          </div>
        </div>
      </div>

      {notice && (
        <p className="mb-4 rounded-lg border border-ink-700 bg-ink-800/60 p-3 text-sm text-ink-200">{notice}</p>
      )}

      <NotesSection
        projectId={projectId}
        experimentId={experimentId}
        notes={notes}
        me={me}
        isOwner={isOwner}
        readOnly={readOnly}
        vocabulary={project.vocabulary ?? []}
      />

      <AttachmentsSection
        projectId={projectId}
        experimentId={experimentId}
        attachments={attachments}
        me={me}
        isOwner={isOwner}
        readOnly={readOnly}
      />

      <section className="mb-6">
        <h2 className="mb-2 text-base font-semibold">Data</h2>
        {!readerMode ? (
          <p className="text-sm text-ink-400">
            Readings captured here are added to the{' '}
            <Link to={`/p/${projectId}`} className="text-accent-400 hover:underline">
              project table
            </Link>
            .
          </p>
        ) : data.length === 0 ? (
          <p className="rounded-lg border border-dashed border-ink-700 px-4 py-5 text-center text-sm text-ink-400">
            {readOnly
              ? 'No data was captured in this experiment.'
              : 'Nothing captured yet. Use the bar below — photo, voice or manual — and it is saved to this experiment.'}
          </p>
        ) : (
          <ReadingsView projectId={projectId} readers={projectReaders} readings={data} canDelete={isOwner} />
        )}
      </section>

      {showCaptureBar && (
        <CaptureBar
          uid={uid}
          projectId={projectId}
          formulations={formulations}
          readers={projectReaders}
          onCaptured={setPendingConfirmation}
          onReaderSession={readerMode ? setReaderSession : undefined}
        />
      )}

      {readerSession && (
        <ReaderCapture projectId={projectId} session={readerSession} onClose={() => setReaderSession(null)} />
      )}

      {pendingConfirmation && (
        <ConfirmationScreen
          projectId={projectId}
          formulations={formulations}
          source={pendingConfirmation}
          onReExtract={setPendingConfirmation}
          onClose={() => setPendingConfirmation(null)}
        />
      )}

      <ExperimentDialog
        projectId={projectId}
        target={editingExperiment ? experiment : null}
        onClose={() => setEditingExperiment(false)}
      />

      <Modal open={confirmDelete} onClose={() => setConfirmDelete(false)} title="Delete this experiment?">
        <p className="text-sm text-ink-300">
          The experiment is removed from the notebook. Data captured in it stays in the project.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
            Keep it
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              await deleteExperiment(projectId, experimentId).catch(() => {});
              navigate(`/p/${projectId}`);
            }}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}

type Me = { uid: string; name: string | null };

function NotesSection({
  projectId,
  experimentId,
  notes,
  me,
  isOwner,
  readOnly,
  vocabulary,
}: {
  projectId: string;
  experimentId: string;
  notes: Note[];
  me: Me;
  isOwner: boolean;
  readOnly: boolean;
  vocabulary: string[];
}) {
  const [draft, setDraft] = useState('');
  const [wordsOpen, setWordsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const wordRef = useRef<HTMLInputElement>(null);

  async function importWord(file: File) {
    setImporting(true);
    setError(null);
    try {
      await importWordNote(projectId, experimentId, file, me);
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not import that Word file.');
    } finally {
      setImporting(false);
    }
  }

  async function add() {
    if (!draft.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await addNote(projectId, experimentId, draft, 'typed', me);
      setDraft('');
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not save the note.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="mb-6">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold">Notes</h2>
        {!readOnly && (
          <>
            <Button
              variant="ghost"
              icon={<FileText size={14} />}
              loading={importing}
              onClick={() => wordRef.current?.click()}
            >
              Word file as note
            </Button>
            <input
              ref={wordRef}
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void importWord(file);
              }}
            />
          </>
        )}
      </div>
      {notes.length === 0 && (
        <p className="mb-3 text-sm text-ink-400">
          {readOnly ? 'No notes yet.' : 'Observations, steps, anything worth remembering — in your own words.'}
        </p>
      )}
      <ul className="mb-3 space-y-2">
        {notes.map((n) => {
          const mine = n.authorUid === me.uid;
          const when = n.createdAt?.toDate?.();
          return (
            <li key={n.id} className="rounded-lg border border-ink-700 bg-ink-800/40 px-3 py-2.5">
              <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-ink-400">
                <span>
                  {when
                    ? when.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                    : ''}
                  {n.pending && ' · saving…'}
                </span>
                {n.authorName && <span>· {n.authorName}</span>}
                {n.source === 'voice' && (
                  <Badge tone="info">
                    <Mic size={10} />
                    spoken
                  </Badge>
                )}
                {n.source === 'document' && (
                  <Badge tone="info">
                    <FileText size={10} />
                    {n.fileName ? `from ${n.fileName}` : 'from Word'}
                  </Badge>
                )}
                {n.editedAt && <span className="italic">edited</span>}
                <span className="ml-auto flex gap-1">
                  {!readOnly && mine && editing?.id !== n.id && (
                    <button
                      className="icon-btn text-ink-400 hover:bg-ink-800 hover:text-ink-100"
                      aria-label="Edit note"
                      onClick={() => setEditing({ id: n.id, text: n.text })}
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                  {!readOnly && (mine || isOwner) && (
                    <button
                      className="icon-btn text-ink-400 hover:bg-ink-800 hover:text-red-300"
                      aria-label="Delete note"
                      onClick={() => {
                        if (window.confirm('Delete this note?')) {
                          void deleteNote(projectId, experimentId, n.id).catch(() => {});
                        }
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </span>
              </div>
              {editing?.id === n.id ? (
                <div className="space-y-2">
                  <Textarea
                    rows={Math.min(20, Math.max(4, editing.text.split('\n').length + 1))}
                    value={editing.text}
                    onChange={(e) => setEditing({ id: n.id, text: e.target.value })}
                    autoFocus
                  />
                  {/* Speaking adds to the end of the note being edited. */}
                  <HoldToTalk
                    projectId={projectId}
                    experimentId={experimentId}
                    me={me}
                    label="Hold to add by voice"
                    onText={(spoken) =>
                      setEditing((cur) =>
                        cur && cur.id === n.id
                          ? { id: n.id, text: cur.text.trimEnd() ? `${cur.text.trimEnd()}\n${spoken}` : spoken }
                          : cur,
                      )
                    }
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => setEditing(null)}>
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      disabled={!editing.text.trim() || editing.text.length > MAX_NOTE_CHARS}
                      onClick={async () => {
                        try {
                          await editNote(projectId, experimentId, n.id, editing.text);
                          setEditing(null);
                        } catch (e) {
                          setError((e as Error)?.message ?? 'Could not save the note.');
                        }
                      }}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="whitespace-pre-wrap text-sm text-ink-100">{n.text}</p>
              )}
            </li>
          );
        })}
      </ul>
      {!readOnly && (
        <div className="space-y-2">
          <HoldToTalk projectId={projectId} experimentId={experimentId} me={me} />
          {isOwner && (
            <p className="text-center text-xs text-ink-400">
              Specialist words misheard?{' '}
              <button className="text-accent-400 hover:underline" onClick={() => setWordsOpen(true)}>
                Words to recognise{vocabulary.length ? ` (${vocabulary.length})` : ''}
              </button>
            </p>
          )}
          <VocabularyDialog
            open={wordsOpen}
            onClose={() => setWordsOpen(false)}
            projectId={projectId}
            words={vocabulary}
          />
          <Textarea
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="…or type a note"
          />
          {error && <p className="text-sm text-red-300">{error}</p>}
          <div className="flex justify-end">
            <Button variant="primary" loading={saving} disabled={!draft.trim()} onClick={() => void add()}>
              Add note
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

function AttachmentsSection({
  projectId,
  experimentId,
  attachments,
  me,
  isOwner,
  readOnly,
}: {
  projectId: string;
  experimentId: string;
  attachments: Attachment[];
  me: Me;
  isOwner: boolean;
  readOnly: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function upload(file: File) {
    setError(null);
    setProgress(0);
    try {
      await uploadAttachment(projectId, experimentId, file, me, setProgress);
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not attach that file.');
    } finally {
      setProgress(null);
    }
  }

  return (
    <section className="mb-6">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold">Documents</h2>
        {!readOnly && (
          <>
            <Button
              variant="secondary"
              icon={<Paperclip size={16} />}
              loading={progress !== null}
              onClick={() => inputRef.current?.click()}
            >
              {progress !== null ? `${Math.round(progress * 100)}%` : 'Attach Word file'}
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void upload(file);
              }}
            />
          </>
        )}
      </div>
      {error && <p className="mb-2 text-sm text-red-300">{error}</p>}
      {!readOnly && (
        <p className="mb-2 text-xs text-ink-400">
          Word files kept as they are, to open or download. To edit one here, use “Word file as note” under
          Notes. PowerPoint goes in Progress Updates.
        </p>
      )}
      {attachments.length === 0 ? (
        <p className="text-sm text-ink-400">No documents attached.</p>
      ) : (
        <ul className="space-y-1.5">
          {attachments.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-2 rounded-lg border border-ink-700 bg-ink-800/40 px-3 py-2"
            >
              {a.kind === 'docx' ? (
                <FileText size={16} className="shrink-0 text-sky-400" />
              ) : (
                <Presentation size={16} className="shrink-0 text-amber-400" />
              )}
              <a
                href={a.downloadUrl}
                target="_blank"
                rel="noreferrer"
                className="min-w-0 flex-1 truncate text-sm text-ink-100 hover:underline pointer-coarse:py-2"
              >
                {a.name}
              </a>
              <span className="shrink-0 text-xs text-ink-400">
                {Math.max(1, Math.round(a.size / 1024))} KB
                {a.uploadedByName ? ` · ${a.uploadedByName}` : ''}
              </span>
              {!readOnly && (a.uploadedByUid === me.uid || isOwner) && (
                <button
                  className="icon-btn text-ink-400 hover:bg-ink-800 hover:text-red-300"
                  aria-label={`Remove ${a.name}`}
                  onClick={() => {
                    if (window.confirm(`Remove ${a.name}?`)) {
                      void deleteAttachment(projectId, experimentId, a).catch(() => {});
                    }
                  }}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
